import dayjs from 'dayjs';
import { OrgOverviewReleaseTypesFilter } from '@/types';
import { MIN_ORG_METRIC_PERIOD_IN_DAYS } from './OrgOverviewDatePickerTimeRange';
import { OrgNavigationDateRangeType } from './OrgNavigationDateRange';
import { OrgNavigationFilters } from './OrgNavigationContent';

const ORG_OVERVIEW_FILTERS_STORAGE_KEY = 'org-overview-v2-filters';

export const getOrgNavigationFiltersKey = (orgId: string): string => {
    return `${ORG_OVERVIEW_FILTERS_STORAGE_KEY}-${orgId}`;
};

export const hasStoredOrgNavigationFilters = (orgId: string): boolean => {
    try {
        return !!window.localStorage.getItem(getOrgNavigationFiltersKey(orgId));
    } catch {
        return false;
    }
};

export const toSerializableOrgNavigationFilters = (filters: OrgNavigationFilters) => {
    return {
        selectedDateRangeType: filters.selectedDateRangeType,
        selectedDateRange: filters.selectedDateRange,
        selectedMetrics: filters.selectedMetrics,
        customBucketSizes: filters.customBucketSizes,
        selectedWorkflowApps: filters.selectedWorkflowApps,
        selectedDestinations: filters.selectedDestinations,
        selectedPlatforms: filters.selectedPlatforms,
        selectedOrgTags: filters.selectedOrgTags,
        selectedUsers: filters.selectedUsers,
        selectedBuildBuckets: filters.selectedBuildBuckets,
        selectedReleaseTypes: filters.selectedReleaseTypes,
        shouldGenerateInsights: filters.shouldGenerateInsights,
    };
};

export const createDefaultOrgNavigationFilters = (): OrgNavigationFilters => {
    const startDate = dayjs().subtract(MIN_ORG_METRIC_PERIOD_IN_DAYS, 'days');
    const endDate = dayjs();

    return {
        selectedDateRangeType: OrgNavigationDateRangeType.Custom,
        selectedDateRange: {
            start: startDate.format('YYYY-MM-DD'),
            end: endDate.format('YYYY-MM-DD'),
        },
        selectedMetrics: [],
        customBucketSizes: {},
        selectedWorkflowApps: {},
        selectedDestinations: {},
        selectedPlatforms: {},
        selectedOrgTags: {},
        selectedUsers: {},
        selectedBuildBuckets: {},
        shouldGenerateInsights: true,
        selectedReleaseTypes: OrgOverviewReleaseTypesFilter.AllReleaseTypes,
    };
};

const parseStoredOrgOverviewFilters = (value: string): OrgNavigationFilters | null => {
    if (!value) {
        return null;
    }

    try {
        return JSON.parse(value) as OrgNavigationFilters;
    } catch {
        return null;
    }
};

export const getStoredOrgNavigationFilters = (orgId: string): OrgNavigationFilters | null => {
    try {
        const storedFilters = window.localStorage.getItem(getOrgNavigationFiltersKey(orgId)) || '';
        const parsedFilters = parseStoredOrgOverviewFilters(storedFilters) || ({} as OrgNavigationFilters);
        const defaultFilters = createDefaultOrgNavigationFilters();

        return {
            ...defaultFilters,
            ...parsedFilters,
        };
    } catch {
        return null;
    }
};

export const setStoredOrgNavigationFilters = (orgId: string, filters: OrgNavigationFilters): void => {
    try {
        window.localStorage.setItem(
            getOrgNavigationFiltersKey(orgId),
            JSON.stringify(toSerializableOrgNavigationFilters(filters)),
        );
    } catch {
        return;
    }
};
